import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, GripVertical, Plus } from "lucide-react";
import { ApiClientError } from "@/api/client";
import {
  KbAdminSection,
  fetchKbAdminSections,
  orderKbSections,
} from "@/api/knowledge";
import { KbErrorCard, KbRowsSkeleton } from "./kbUi";
import { KbSectionDrawer } from "./KbSectionDrawer";
import { KbMoveModal } from "./KbMoveModal";

/**
 * D-147 W1 S5 — «Розділи бази знань» (visual package ⑥). KB_Admin бачить
 * усі розділи + create/order; owner без KB_Admin — свої (drawer-настройки).
 * Порядок: drag (HTML5) + КЛАВІАТУРНА альтернатива (кнопки «Вгору/Вниз»,
 * A11-guardrail) — обидва шляхи POST admin/sections/order.
 */
export function KnowledgeAdminPage() {
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [drawer, setDrawer] = useState<{ mode: "create" } | { mode: "edit"; section: KbAdminSection } | null>(null);
  const [moveFor, setMoveFor] = useState<KbAdminSection | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["kbAdminSections"],
    queryFn: fetchKbAdminSections,
    staleTime: 15_000,
    retry: (count, e) => !(e instanceof ApiClientError && (e.status === 403 || e.status === 404)) && count < 2,
  });

  const isKbAdmin = q.data?.isKbAdmin ?? false;
  const sections = useMemo(
    () => (q.data?.sections ?? []).filter((s) => showArchived || !s.isArchived),
    [q.data?.sections, showArchived],
  );

  const denied = q.error instanceof ApiClientError && q.error.status === 403;

  const persistOrder = (ids: string[]) => {
    setOrderError(null);
    orderKbSections(ids).catch(() => setOrderError("Не вдалося зберегти порядок — повторіть."));
  };

  const moveBy = (id: string, delta: -1 | 1) => {
    const ids = (q.data?.sections ?? []).map((s) => s.id);
    const idx = ids.indexOf(id);
    const to = idx + delta;
    if (idx < 0 || to < 0 || to >= ids.length) return;
    [ids[idx], ids[to]] = [ids[to], ids[idx]];
    qc.setQueryData(["kbAdminSections"], (old: typeof q.data) =>
      old
        ? { ...old, sections: ids.map((sid) => old.sections.find((s) => s.id === sid)!) }
        : old,
    );
    persistOrder(ids);
  };

  const dropOn = (targetId: string) => {
    if (!dragId || dragId === targetId) return;
    const ids = (q.data?.sections ?? []).map((s) => s.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    qc.setQueryData(["kbAdminSections"], (old: typeof q.data) =>
      old
        ? { ...old, sections: ids.map((sid) => old.sections.find((s) => s.id === sid)!) }
        : old,
    );
    persistOrder(ids);
    setDragId(null);
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-[1080px]">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <nav className="mb-1 text-xs text-muted-foreground" aria-label="Хлібні крихти">
              <Link to="/knowledge" className="text-primary hover:underline">
                База знань
              </Link>
              <span> / Налаштування</span>
            </nav>
            <h1 className="text-xl font-semibold tracking-tight">Розділи бази знань</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="h-3.5 w-3.5 accent-primary"
              />
              Показати архівні
            </label>
            {isKbAdmin && (
              <button
                type="button"
                onClick={() => setDrawer({ mode: "create" })}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground"
              >
                <Plus className="h-4 w-4" aria-hidden /> Створити розділ
              </button>
            )}
          </div>
        </div>

        {orderError && (
          <p role="alert" className="mb-3 rounded-lg bg-destructive-soft px-3 py-2 text-xs text-destructive-soft-foreground">
            {orderError}
          </p>
        )}

        {q.isPending ? (
          <KbRowsSkeleton rows={3} />
        ) : denied ? (
          <div className="rounded-2xl border border-dashed bg-card px-4 py-12 text-center">
            <div className="text-sm font-medium">
              Налаштування доступні власникам розділів і адміністраторам бази знань
            </div>
            <Link
              to="/knowledge"
              className="mt-3 inline-block rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground"
            >
              До Бази знань
            </Link>
          </div>
        ) : q.isError ? (
          <KbErrorCard text="Не вдалося завантажити розділи" onRetry={() => void q.refetch()} />
        ) : sections.length === 0 ? (
          <div className="rounded-2xl border border-dashed bg-card px-4 py-12 text-center">
            <div className="text-sm font-medium">Розділів ще немає</div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-card">
            {sections.map((s) => (
              <div
                key={s.id}
                draggable={isKbAdmin}
                onDragStart={() => setDragId(s.id)}
                onDragOver={(e) => isKbAdmin && e.preventDefault()}
                onDrop={() => dropOn(s.id)}
                className="flex items-start gap-3 border-b px-4 py-3.5 last:border-b-0"
              >
                {isKbAdmin && (
                  <span
                    className="mt-0.5 cursor-grab text-muted-foreground"
                    title="Перетягнути; клавіатура: кнопки «Вгору/Вниз»"
                    aria-hidden
                  >
                    <GripVertical className="h-4 w-4" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/knowledge/sections/${s.id}`} className="text-sm font-bold hover:underline">
                      {s.name}
                    </Link>
                    {s.isArchived && (
                      <span className="inline-flex items-center rounded-full border bg-muted px-2 py-px text-[11px] font-semibold text-muted-foreground">
                        архів
                      </span>
                    )}
                    {s.visibilityMode === "all" ? (
                      <span className="inline-flex items-center rounded-full bg-info px-2 py-px text-[11px] font-semibold text-info-foreground">
                        видимість: усі
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-violet px-2 py-px text-[11px] font-semibold text-violet-foreground">
                        видимість: команди ({s.teams.filter((t) => !t.missing).length})
                      </span>
                    )}
                    <span className="inline-flex items-center rounded-full border bg-muted px-2 py-px text-[11px] font-semibold text-muted-foreground">
                      {s.publishedCount} опубл.{s.draftCount > 0 ? ` · ${s.draftCount} чернетки` : ""}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                    <span>
                      Власник: <b className="text-foreground">{s.owner?.name ?? "—"}</b>
                    </span>
                    {s.backupOwner && (
                      <span>
                        Резерв: <b className="text-foreground">{s.backupOwner.name}</b> (повні права власника)
                      </span>
                    )}
                    <span>
                      Редактори:{" "}
                      <b className="text-foreground">
                        {s.editors.length > 0 ? s.editors.map((e) => e.name).join(", ") : "—"}
                      </b>
                    </span>
                  </div>
                </div>
                <div className="flex flex-none items-center gap-1.5">
                  {isKbAdmin && (
                    <>
                      <button
                        type="button"
                        aria-label={`${s.name}: вгору`}
                        onClick={() => moveBy(s.id, -1)}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        aria-label={`${s.name}: вниз`}
                        onClick={() => moveBy(s.id, 1)}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => setDrawer({ mode: "edit", section: s })}
                    className="rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-accent"
                  >
                    Налаштування
                  </button>
                  {!s.isArchived && (
                    <button
                      type="button"
                      onClick={() => setMoveFor(s)}
                      className="rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      Перемістити матеріали…
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {drawer && (
          <KbSectionDrawer
            section={drawer.mode === "edit" ? drawer.section : null}
            isKbAdmin={isKbAdmin}
            onClose={() => setDrawer(null)}
          />
        )}
        {moveFor && (
          <KbMoveModal
            source={moveFor}
            candidates={(q.data?.sections ?? []).filter((s) => s.id !== moveFor.id && !s.isArchived)}
            onClose={() => setMoveFor(null)}
          />
        )}
      </div>
    </div>
  );
}
